// src/modules/clinical-notes-v2/components/ClinicalDataDialog.jsx
//
// Modal opcional con el resumen clínico del paciente (medications,
// labs, allergies, chronic conditions). El doctor abre este Dialog
// desde un botón en el header de la página v2 cuando quiere consultar
// datos — la página principal queda enfocada en care gaps + coding
// opps + el form de la nota.

import React from "react";
import {
  Alert,
  Avatar,
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";

import CloseIcon         from "@mui/icons-material/Close";
import MedicationIcon    from "@mui/icons-material/Medication";
import ScienceIcon       from "@mui/icons-material/Science";
import WarningAmberIcon  from "@mui/icons-material/WarningAmber";
import MonitorHeartIcon  from "@mui/icons-material/MonitorHeart";

const ABNORMAL_FLAGS = new Set(["H", "L", "HH", "LL", "A", "AB", "ABNORMAL"]);

function TabPanel({ value, index, children }) {
  if (value !== index) return null;
  return <Box sx={{ pt: 2 }}>{children}</Box>;
}

function fmtDate(d) {
  if (!d) return "—";
  const x = new Date(d);
  if (Number.isNaN(x.getTime())) return String(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
}

export default function ClinicalDataDialog({ open, onClose, bundle }) {
  const [tab, setTab] = React.useState(0);

  const meds      = bundle?.activeMedications || [];
  const labs      = bundle?.recentLabs || [];
  const allergies = bundle?.allergies || [];
  const chronic   = bundle?.chronicConditions || [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ pr: 7, fontWeight: 800 }}>
        Clinical data — patient summary
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: "absolute", right: 12, top: 10 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          px: 3,
          "& .MuiTab-root": { textTransform: "none", fontWeight: 700, minHeight: 44 },
        }}
      >
        <Tab icon={<MedicationIcon fontSize="small" />} iconPosition="start" label={`Medications (${meds.length})`} />
        <Tab icon={<ScienceIcon fontSize="small" />}    iconPosition="start" label={`Labs (${labs.length})`} />
        <Tab icon={<WarningAmberIcon fontSize="small" />} iconPosition="start" label={`Allergies (${allergies.length})`} />
        <Tab icon={<MonitorHeartIcon fontSize="small" />} iconPosition="start" label={`Chronic (${chronic.length})`} />
      </Tabs>

      <DialogContent dividers sx={{ minHeight: 320 }}>
        <TabPanel value={tab} index={0}>
          {meds.length === 0 ? (
            <EmptyHint text="No active medications on file." />
          ) : (
            <List dense disablePadding>
              {meds.map((m, i) => (
                <ListItem key={i} disableGutters sx={{ borderBottom: "1px dashed", borderColor: "divider", py: 1 }}>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: "primary.50", color: "primary.main", width: 36, height: 36 }}>
                      <MedicationIcon fontSize="small" />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={<Typography fontWeight={700}>{m.name || "—"}</Typography>}
                    secondary={
                      <Stack direction="row" spacing={1} sx={{ mt: 0.25, flexWrap: "wrap" }}>
                        {m.dose && <Chip size="small" variant="outlined" label={m.dose} />}
                        {m.startDate && <Chip size="small" variant="outlined" label={`Start ${fmtDate(m.startDate)}`} />}
                        {m.stopDate  && <Chip size="small" variant="outlined" color="warning" label={`Stop ${fmtDate(m.stopDate)}`} />}
                      </Stack>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </TabPanel>

        <TabPanel value={tab} index={1}>
          {labs.length === 0 ? (
            <EmptyHint text="No labs available for this patient." />
          ) : (
            <List dense disablePadding>
              {labs.map((l, i) => {
                const flag = (l.abnormalFlag || "").toString().toUpperCase();
                const isAbnormal = ABNORMAL_FLAGS.has(flag);
                return (
                  <ListItem key={i} disableGutters sx={{ borderBottom: "1px dashed", borderColor: "divider", py: 1 }}>
                    <ListItemAvatar>
                      <Avatar
                        sx={{
                          bgcolor: isAbnormal ? "error.50" : "info.50",
                          color:   isAbnormal ? "error.main" : "info.main",
                          width: 36, height: 36,
                        }}
                      >
                        <ScienceIcon fontSize="small" />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography fontWeight={700}>{l.name || "—"}</Typography>
                          <Typography variant="body2" fontWeight={700} color={isAbnormal ? "error.main" : "text.primary"}>
                            {l.value ?? "—"}{l.unit ? " " + l.unit : ""}
                          </Typography>
                          {isAbnormal && <Chip size="small" color="error" label={flag} />}
                        </Stack>
                      }
                      secondary={
                        <Stack direction="row" spacing={1} sx={{ mt: 0.25, flexWrap: "wrap" }}>
                          {l.date          && <Chip size="small" variant="outlined" label={fmtDate(l.date)} />}
                          {l.referenceRange && <Chip size="small" variant="outlined" label={`Ref ${l.referenceRange}`} />}
                          {l.companyName    && <Chip size="small" variant="outlined" label={l.companyName} />}
                        </Stack>
                      }
                    />
                  </ListItem>
                );
              })}
            </List>
          )}
        </TabPanel>

        <TabPanel value={tab} index={2}>
          {allergies.length === 0 ? (
            <EmptyHint text="No known allergies." />
          ) : (
            <List dense disablePadding>
              {allergies.map((a, i) => (
                <ListItem key={i} disableGutters sx={{ borderBottom: "1px dashed", borderColor: "divider", py: 1 }}>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: "warning.50", color: "warning.main", width: 36, height: 36 }}>
                      <WarningAmberIcon fontSize="small" />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={<Typography fontWeight={700}>{a.substance || "—"}</Typography>}
                    secondary={
                      <Stack direction="row" spacing={1} sx={{ mt: 0.25, flexWrap: "wrap" }}>
                        {a.reaction  && <Chip size="small" variant="outlined" color="warning" label={`Reaction: ${a.reaction}`} />}
                        {a.severity  && <Chip size="small" variant="outlined" label={`Severity: ${a.severity}`} />}
                        {a.treatment && <Chip size="small" variant="outlined" label={`Tx: ${a.treatment}`} />}
                      </Stack>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </TabPanel>

        <TabPanel value={tab} index={3}>
          {chronic.length === 0 ? (
            <EmptyHint text="No chronic conditions on file." />
          ) : (
            <List dense disablePadding>
              {chronic.map((c, i) => (
                <ListItem key={i} disableGutters sx={{ borderBottom: "1px dashed", borderColor: "divider", py: 1 }}>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: "secondary.50", color: "secondary.main", width: 36, height: 36 }}>
                      <MonitorHeartIcon fontSize="small" />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={<Typography fontWeight={700}>{c.description || c.icd10 || "—"}</Typography>}
                    secondary={c.icd10 ? <Chip size="small" variant="outlined" label={c.icd10} /> : null}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </TabPanel>
      </DialogContent>
    </Dialog>
  );
}

function EmptyHint({ text }) {
  return (
    <Alert severity="info" variant="outlined" sx={{ borderStyle: "dashed" }}>
      {text}
    </Alert>
  );
}
